import { ArrowType } from '@ardoq/api-types';
import { Point } from '@ardoq/math';
import { HtmlCanvasVisual, IEdge, IRenderContext } from '@ardoq/yfiles';
import {
  anchorDashes,
  type PathPiece,
} from 'tabview/canvasRendering/anchoredDashes';
import { drawMarker } from 'tabview/canvasRendering/drawMarker';
import { SMOOTH_PATH_OPTIONS } from 'tabview/relationshipDiagrams/consts';
import { createPath, toPathPieces } from 'yfilesExtensions/edgeUtil';
import {
  MARKER_SIZE,
  MARKER_STROKE_WIDTH,
  markerAnchorOffset,
} from './markerMetrics';

const tracePiece = (ctx: CanvasRenderingContext2D, piece: PathPiece) => {
  const [x, y] = piece.to;
  if (piece.type === 'line') {
    ctx.lineTo(x, y);
    return;
  }
  const [cpx, cpy] = piece.control;
  ctx.quadraticCurveTo(cpx, cpy, x, y);
};

const strokeSolid = (ctx: CanvasRenderingContext2D, pieces: PathPiece[]) => {
  ctx.beginPath();
  if (pieces.length) {
    const [x, y] = pieces[0].from;
    ctx.moveTo(x, y);
  }
  pieces.forEach(piece => tracePiece(ctx, piece));
  ctx.stroke();
};

const strokeDashed = (
  ctx: CanvasRenderingContext2D,
  pieces: PathPiece[],
  dashArray: number[]
) =>
  anchorDashes(pieces, dashArray).forEach(({ piece, dashOffset }) => {
    ctx.beginPath();
    const [x, y] = piece.from;
    ctx.moveTo(x, y);
    tracePiece(ctx, piece);
    ctx.lineDashOffset = dashOffset;
    ctx.stroke();
  });

class CanvasEdgeVisual extends HtmlCanvasVisual {
  private pieces: PathPiece[];
  constructor(
    private edge: IEdge,
    private stroke: string,
    private dashArray: number[],
    private lineBeginning: ArrowType,
    private lineEnding: ArrowType,
    private opacity: number
  ) {
    super();
    this.pieces = toPathPieces(
      createPath(this.edge, false).createSmoothedPath(SMOOTH_PATH_OPTIONS)
    );
  }
  render(_: IRenderContext, ctx: CanvasRenderingContext2D): void {
    const { globalAlpha } = ctx;
    ctx.globalAlpha = this.opacity;

    ctx.lineWidth = 1;
    ctx.strokeStyle = this.stroke;
    ctx.setLineDash(this.dashArray);
    if (this.dashArray.length) {
      strokeDashed(ctx, this.pieces, this.dashArray);
      ctx.lineDashOffset = 0;
    } else {
      strokeSolid(ctx, this.pieces);
    }
    ctx.setLineDash([]);

    const startPoint = this.edge.sourcePort!.location;
    const { x: startX, y: startY } = startPoint;
    const endPoint = this.edge.targetPort!.location;
    const { x: endX, y: endY } = endPoint;

    const { x: secondPointX, y: secondPointY } = this.edge.bends.size
      ? this.edge.bends.first()!.location
      : endPoint;
    const { x: penultimatePointX, y: penultimatePointY } = this.edge.bends.size
      ? this.edge.bends.last()!.location
      : startPoint;

    const startAngle = Math.atan2(startY - secondPointY, startX - secondPointX);
    const endAngle = Math.atan2(
      endY - penultimatePointY,
      endX - penultimatePointX
    );
    const startOffset = markerAnchorOffset(this.lineBeginning);
    const endOffset = markerAnchorOffset(this.lineEnding);
    const startMarkerLocation: Point = [
      startX - startOffset * Math.cos(startAngle),
      startY - startOffset * Math.sin(startAngle),
    ];
    const endMarkerLocation: Point = [
      endX - endOffset * Math.cos(endAngle),
      endY - endOffset * Math.sin(endAngle),
    ];
    const { lineJoin } = ctx;
    ctx.lineJoin = 'round';
    ctx.lineWidth = MARKER_STROKE_WIDTH;
    drawMarker(
      ctx,
      this.lineBeginning,
      startMarkerLocation,
      startAngle,
      MARKER_SIZE
    );
    drawMarker(ctx, this.lineEnding, endMarkerLocation, endAngle, MARKER_SIZE);
    ctx.lineJoin = lineJoin;
    ctx.globalAlpha = globalAlpha;
  }
}
export { CanvasEdgeVisual };
